import { useState, useEffect, type KeyboardEvent } from 'react'
import ReactSelect, { components, Options } from 'react-select'
import AsyncSelect from 'react-select/async'
import { useDebouncedCallback } from 'use-debounce'
import { Box, Icons, Modal, Button } from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import { getField } from 'stylewhere/utils'
import styled from '@emotion/styled'
import { Global, css } from '@emotion/react'
import { openSelectDropdown, closeSelectDropdown } from 'stylewhere/shared'

const StyledLabel = styled.label<{
  isFloating: boolean
  isFocused: boolean
  alwaysVisible?: boolean
  disabled?: boolean
}>`
  position: absolute;
  left: 15px;
  top: ${({ isFloating }) => (isFloating ? '16px' : '50%')};
  transform: translateY(-50%);
  font-size: ${({ isFloating }) => (isFloating ? '16px' : '18px')};
  color: '#666666';
  padding: 0 5px;
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  z-index: 1;
  line-height: 1;
  opacity: ${({ isFloating, alwaysVisible, disabled }) => (isFloating || alwaysVisible ? (disabled ? 0.5 : 1) : 0)};
`

type OptionConfig = { label: string; value: string; secondaryLabel?: string; concatSecondaryLabel?: boolean }

const maxFontSize = 22
const minFontSize = 16
const maxCharacters = 22
const decrement = 0.3

interface SelectProps {
  placeholder?: React.ReactNode
  defaultValue?: any
  containerStyle?: React.CSSProperties
  controlStyle?: React.CSSProperties
  disabled?: boolean
  options?: any[]
  asyncOptions?: (inputValue: string, callback: (options: Options<Record<string, any>>) => void) => void
  debounce?: boolean
  config: OptionConfig
  borderColor?: string
  onSelect: (item?: any) => void
  transparent?: boolean
  multiple?: boolean
  value?: any
  useButton?: boolean
  searchDisabled?: boolean
  isModalOpen?: boolean
  customComponents?: any
  isClearable?: boolean
  label?: string
}

const Control: typeof components.Control = ({ children, ...props }) => {
  const testId = props.selectProps['data-testid']
  const isDisabled = props.isDisabled
  return (
    <components.Control
      {...props}
      innerProps={
        {
          ...props.innerProps,
          'data-testid': testId,
          'data-cy': testId,
          disabled: isDisabled || undefined
        } as any
      }
    >
      {children}
    </components.Control>
  )
}

const selectOptionInteractionStyles = css`
  .stw-select-option {
    transition: box-shadow 0.2s ease !important;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
    background-color: #f5f5f5 !important;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    }

    &:active {
      box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.2) !important;
    }
  }
`

const Option: typeof components.Option = ({ children, ...props }) => (
  <components.Option
    {...props}
    innerProps={
      {
        ...props.innerProps,
        className: [props.innerProps?.className, 'stw-select-option'].filter(Boolean).join(' '),
        'data-testid': `select-option`,
        'data-cy': typeof children === 'string' ? children : undefined
      } as any
    }
  >
    <Box flex row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <span
        style={{ overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '18px' }}
        title={typeof children === 'string' ? children : undefined}
      >
        {children}
      </span>
      {props.isSelected && <Icons.CheckFilled />}
    </Box>
  </components.Option>
)

const ClearIndicator: typeof components.ClearIndicator = (props) => (
  <components.ClearIndicator
    {...props}
    innerProps={
      {
        ...props.innerProps,
        'data-testid': 'select-clear',
        'data-cy': 'select-clear'
      } as any
    }
  >
    <Icons.Close />
  </components.ClearIndicator>
)

const MenuList: typeof components.MenuList = (props) => (
  <components.MenuList {...props}>
    <div data-testid={`select-menu-list`} data-cy={`select-menu-list`}>
      {props.children}
    </div>
  </components.MenuList>
)

const searchStyles = {
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
    display: 'flex',
    alignItems: 'center'
  }),

  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#333333',
    cursor: 'text',
    caretColor: 'auto',
    '& input': {
      cursor: 'text',
      caretColor: 'auto'
    }
  })
}

export const Select: React.FC<SelectProps> = ({
  onSelect,
  config,
  placeholder,
  defaultValue,
  containerStyle,
  controlStyle,
  options,
  asyncOptions,
  debounce = true,
  borderColor = '#D2D2D2',
  disabled,
  transparent,
  multiple = false,
  value,
  useButton,
  searchDisabled,
  isModalOpen = false,
  customComponents,
  isClearable = true,
  label
}) => {
  const [open, setOpen] = useState(false)
  const [firstRender, setFirstRender] = useState(true)
  const [isFocused, setIsFocused] = useState(false)
  const [modalSearch, setModalSearch] = useState('')
  const debouncedAsyncOptions = useDebouncedCallback(asyncOptions ?? (() => {}), 400, { leading: true })

  useEffect(() => {
    setFirstRender(false)
  }, [])

  useEffect(() => {
    if (isModalOpen) {
      openDropdownModal()
    }
  }, [isModalOpen])

  const openDropdownModal = () => {
    openSelectDropdown()
    setOpen(true)
  }

  const styles = {
    container: (base) => ({
      ...base,
      border: 0,
      ...containerStyle
    }),
    control: (base, state) => ({
      ...base,
      height: 65,
      fontSize: getFontSize(state.label, decrement),
      paddingLeft: 7,
      backgroundColor: transparent ? 'transparent' : '#ffffff',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(47, 128, 237, 0.5)' : 'none',
      borderWidth: 2,
      borderColor: state.isFocused ? '#2f80ed' : borderColor,
      display: 'flex',
      borderRadius: 10,
      '&:hover': {
        borderColor: state.isFocused ? '#2f80ed' : borderColor
      },
      ...controlStyle
    }),
    input: (base) => ({
      ...base,
      color: '#333333',
      cursor: 'text',
      caretColor: 'auto',
      '& input': {
        cursor: 'text',
        caretColor: 'auto'
      },
      margin: 0,
      padding: 0
    }),
    placeholder: (base) => ({
      ...base,
      color: '#666666'
    }),
    menu: (base) => ({
      ...base,
      position: 'relative',
      border: 0,
      boxShadow: 0,
      marginTop: searchDisabled ? 0 : '1rem'
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
      maxHeight: 350
    }),
    option: (base, state) => ({
      ...base,
      fontWeight: 500,
      fontSize: getFontSize(state.label, decrement),
      lineHeight: '48px',
      borderRadius: 10,
      height: 70,
      color: '#333333',
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: state.isSelected ? '#000000' : '#F5F5F5',
      padding: '10px 20px',
      backgroundColor: '#F5F5F5',
      boxShadow: 'none',
      outline: 'none',
      marginBottom: '1rem',
      cursor: 'pointer'
    }),
    singleValue: (base) => ({
      ...base,
      color: 'black',
      marginLeft: 0,
      marginRight: 0,
      fontSize: '18px'
    }),
    indicatorSeparator: () => ({}),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: 10,
      '& svg': { width: 26, height: 26 }
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  }

  const closeModal = () => {
    closeSelectDropdown()
    setModalSearch('')
    setOpen(false)
  }

  const onChange = (selectedItems) => {
    if (selectedItems?.length === 0) {
      onSelect(undefined)
    } else if (selectedItems) {
      onSelect(selectedItems)
    }
    !multiple && closeModal()
  }

  const getOptionLabel = (option: Record<string, any>) => {
    try {
      const optionLabel = getField(option, config.label)
      if (optionLabel) {
        if (config.concatSecondaryLabel && config.secondaryLabel) {
          return optionLabel + ' - ' + getField(option, config.secondaryLabel)
        }
        return optionLabel
      }
      return config.secondaryLabel ? getField(option, config.secondaryLabel) : ''
    } catch {
      return ''
    }
  }

  const normalizeSearchValue = (input: unknown) => {
    if (typeof input !== 'string' && typeof input !== 'number') return ''
    return String(input).trim().toLowerCase()
  }

  const getComparableOptionValues = (option: Record<string, any>) => [
    getField(option, config.label),
    config.secondaryLabel ? getField(option, config.secondaryLabel) : undefined,
    getField(option, config.value),
    getOptionLabel(option)
  ]

  const findOptionBySearch = (optionsToSearch: Record<string, any>[], search: string) => {
    const normalizedSearch = normalizeSearchValue(search)
    return optionsToSearch.find((option) =>
      getComparableOptionValues(option).some((optionValue) => normalizeSearchValue(optionValue) === normalizedSearch)
    )
  }

  const handleAsyncSelectEnter = (event: KeyboardEvent) => {
    if (!asyncOptions || multiple || event.key !== 'Enter') return

    const target = event.target as HTMLInputElement
    const search = normalizeSearchValue(target?.value || modalSearch)
    if (!search) return

    event.preventDefault()
    event.stopPropagation()

    asyncOptions(search, (results) => {
      const resultList = Array.isArray(results) ? results : []
      const selectedOption =
        findOptionBySearch(resultList, search) ?? (resultList.length === 1 ? resultList[0] : undefined)
      if (selectedOption) {
        onChange(selectedOption)
      }
    })
  }

  const handleModalInputChange = (inputValue: string, actionMeta: { action: string }) => {
    if (actionMeta.action === 'input-change') {
      setModalSearch(inputValue)
    }
    return inputValue
  }

  const getFontSize = (_value?: any, _d = 0.5) => {
    const fontSizeLabel: string = _value ?? getOptionLabel(value) ?? ''
    if (fontSizeLabel.length <= maxCharacters) return maxFontSize
    const calcFontSize = maxFontSize - (fontSizeLabel.length - maxCharacters) * _d
    if (calcFontSize > minFontSize) return calcFontSize
    return minFontSize
  }
  const initValue = firstRender ? defaultValue : value

  const modalSelectProps = {
    isClearable: true,
    isMulti: multiple,
    isSearchable: !searchDisabled,
    styles,
    getOptionLabel,
    getOptionValue: (option) => getField(option, config.value),
    defaultValue: typeof initValue === 'string' ? { label: initValue, value: initValue } : initValue,
    onChange,
    onInputChange: handleModalInputChange,
    onKeyDown: handleAsyncSelectEnter,
    value: typeof initValue === 'string' ? { label: initValue, value: initValue } : initValue,
    autoFocus: true,
    menuIsOpen: true,
    placeholder: __(T.misc.select_search),
    components: {
      Control,
      ClearIndicator,
      DropdownIndicator: null,
      Option,
      MenuList,
      ...customComponents
    },
    noOptionsMessage: () => __(T.misc.no_result),
    'data-testid': `select-${config.label}`,
    'data-cy': `select-${config.label}`
  }

  const selectStyle = {
    ...styles,
    container: (base, state) => ({
      ...base,
      border: `2px solid ${state.isDisabled ? '#eeeeee' : borderColor}`,
      borderRadius: 10
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: '#eeeeee'
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 6,
      backgroundColor: '#eeeeee'
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: 0,
      borderRadius: 6,
      backgroundColor: '#eeeeee'
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: 6,
      backgroundColor: '#eeeeee'
    }),
    control: (base, state) => ({
      ...base,
      paddingTop: multiple && state.hasValue ? 15 : 0,
      minHeight: 61,
      fontSize: getFontSize(),
      paddingLeft: state.hasValue ? 5 : 7,
      backgroundColor: transparent ? 'transparent' : '#ffffff',
      boxShadow: 'none',
      borderWidth: 0,
      borderRadius: 10,
      cursor: 'pointer',
      display: 'flex',
      ...controlStyle
    }),
    placeholder: (base, state) => ({
      ...base,
      color: state.isDisabled ? '#eeeeee' : '#666666'
    }),
    singleValue: (base, state) => ({
      ...base,
      marginTop: state.hasValue ? 18 : 0,
      marginLeft: 0,
      marginRight: 0,
      fontSize: '18px'
    }),
    input: (base) => ({
      ...base,
      marginLeft: 0,
      marginRight: 0,
      caretColor: 'auto',
      cursor: 'text',
      '& input': {
        cursor: 'text',
        caretColor: 'auto'
      }
    })
  }

  const Component = asyncOptions && debounce ? AsyncSelect : ReactSelect
  const hasValue = Array.isArray(value) ? value.length > 0 : !!value

  return (
    <>
      {open && <Global styles={selectOptionInteractionStyles} />}
      {open && (
        <Modal
          data-testid={`select-modal`}
          data-cy={`select-modal`}
          visible={open}
          onClose={closeModal}
          bypassSelectDropdownLock
          title={placeholder}
          size="xl"
          closeOnOverlayClick
        >
          <div style={{ minHeight: 435 }}>
            {asyncOptions && (
              <AsyncSelect
                {...modalSelectProps}
                styles={{
                  ...styles,
                  ...searchStyles
                }}
                loadOptions={debounce ? debouncedAsyncOptions : asyncOptions}
                defaultOptions
                cacheOptions
                loadingMessage={() => null}
                options={options}
                onMenuClose={() => {}}
              />
            )}
            {!asyncOptions && <ReactSelect {...modalSelectProps} options={options} />}
          </div>
        </Modal>
      )}
      {useButton ? (
        <Button
          data-testid={`select-button`}
          data-cy={`select-button`}
          title={__(T.misc.change)}
          onClick={() => {
            openDropdownModal()
          }}
          variant="secondary"
        />
      ) : (
        <Box style={{ position: 'relative' }}>
          {label && (
            <StyledLabel
              isFloating={isFocused || hasValue}
              isFocused={isFocused}
              alwaysVisible={true}
              disabled={disabled}
            >
              {label}
            </StyledLabel>
          )}
          <Component
            styles={{
              ...styles,
              ...selectStyle,
              placeholder: (base, state) => ({
                ...selectStyle.placeholder(base, state),
                color: '#666666',
                opacity: label ? 0 : 1
              })
            }}
            placeholder={placeholder}
            value={typeof initValue === 'string' ? { label: initValue, value: initValue } : initValue}
            options={options || []}
            getOptionLabel={getOptionLabel}
            getOptionValue={typeof initValue === 'string' ? (option) => getField(option, config.value) : undefined}
            onMenuOpen={openDropdownModal}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(s, d) =>
              d.action === 'remove-value'
                ? onChange((value ?? []).filter((v) => getOptionLabel(d.removedValue) !== getOptionLabel(v)))
                : onChange([])
            }
            components={{ Control, Option, ClearIndicator, MenuList, ...customComponents }}
            isDisabled={disabled}
            isMulti={multiple}
            isSearchable={!searchDisabled}
            isClearable={isClearable}
            menuIsOpen={isModalOpen}
            data-testid={`select-${config.label}`}
            data-cy={`select-${config.label}`}
          />
        </Box>
      )}
    </>
  )
}
